import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { useGetShipmentApiV1ShipmentsShipmentIdGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { cn } from '#/shared/lib/utils'
import { Skeleton } from '#/shared/ui/skeleton'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'

import {
  formatCustomer,
  formatShipmentStatus,
  getShipmentDispatchTargets,
  postedBadgeLabelKey,
} from '#/entities/shipment'

function HeaderSkeleton() {
  return (
    <div className="flex flex-col gap-1.25">
      <div className="flex h-5 items-center">
        <Skeleton className="h-3.5 w-24" />
      </div>
      <div className="flex h-6 items-center">
        <Skeleton className="h-3.5 w-36" />
      </div>
    </div>
  )
}

const PILL_CLASS_BY_STATUS: Partial<Record<string, string>> = {
  [ShipmentStatus.posted]: 'bg-orange-50 text-orange-500',
  [ShipmentStatus.dispatched]: 'bg-green-50 text-green-700',
}

const PILL_DEFAULT_CLASS = 'bg-warm-50 text-gray-700'

const PILL_LABEL_KEY_BY_STATUS: Partial<Record<string, string>> = {
  [ShipmentStatus.picked_up]: 'detailToolbar.markAsOptions.pickedUp',
}

function StatusPill({ shipment }: { shipment: ShipmentDetail }) {
  const { t } = useTranslation()
  const labelKey =
    shipment.status === ShipmentStatus.posted
      ? postedBadgeLabelKey(getShipmentDispatchTargets(shipment))
      : PILL_LABEL_KEY_BY_STATUS[shipment.status]
  const label = labelKey ? t(labelKey) : formatShipmentStatus(shipment.status)

  if (!label) return null
  return (
    <span
      className={cn(
        'rounded px-1.25 py-0.5 text-sm',
        PILL_CLASS_BY_STATUS[shipment.status] ?? PILL_DEFAULT_CLASS,
      )}
    >
      {label}
    </span>
  )
}

function CopyableCode({ code }: { code: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
    } catch {
      return
    }
    setCopied(true)
    setOpen(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 2000)
  }

  return (
    <TooltipProvider delayDuration={200}>
      <TooltipRoot open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="cursor-pointer text-sm text-gray-900 transition-colors select-none hover:text-primary"
          >
            {code}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {copied
            ? t('detailToolbar.idCopied', { id: code })
            : t('detailToolbar.copyHint')}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}

export function ShipmentSidebarHeader({ shipmentId }: { shipmentId: string }) {
  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(shipmentId)
  const shipment = shipmentQuery.data?.data

  if (!shipment) return <HeaderSkeleton />

  const customerName = formatCustomer(shipment.customer).name

  return (
    <div className="flex flex-col gap-1.25">
      <p className="truncate text-sm font-medium text-gray-900">
        {customerName}
      </p>
      <div className="flex items-center gap-2.5">
        <CopyableCode code={shipment.code} />
        <StatusPill shipment={shipment} />
      </div>
    </div>
  )
}
